import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "../../../assets/styles/invoiceprint.css";
import { PRINT_TEXT } from "@utils/constants/print.constants";
import { useGetPrimarySaleOrderInvoicesPrintQuery } from "@states/primary-sale-order/primary-invoice.api";
import logo from "../../../assets/images/print_logo.png";
import { formatDBDate } from "@utils/general/format-db-date";
import { thousandSeperator } from "@utils/general/digit-separators";
import logoWithText from "@assets/images/Logo.jpg";

const DirectSaleOrderInvoicePrintPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: primarySaleOrderInvoiceData, isFetching } =
    useGetPrimarySaleOrderInvoicesPrintQuery({
      id: id!,
    });

  const retrieveValues = {
    invoice_date: primarySaleOrderInvoiceData?.data[0]?.invoice_date,
    due_date: primarySaleOrderInvoiceData?.data[0]?.due_date,
    SSIC: primarySaleOrderInvoiceData?.data[0]?.secondary_sales_invoice_code,
    SSOC: primarySaleOrderInvoiceData?.data[0]?.secondary_sales_order_code,
    grq_sale_order_no: primarySaleOrderInvoiceData?.data[0]?.reference_id,
    sales_person_name: primarySaleOrderInvoiceData?.data[0]?.sales_person_name,
    approved_person_name:
      primarySaleOrderInvoiceData?.data[0]?.approved_person_name,
    delivery_date: primarySaleOrderInvoiceData?.data[0]?.delivery_date,
    customer_name: primarySaleOrderInvoiceData?.data[0]?.customer_name,
    customer_phone: primarySaleOrderInvoiceData?.data[0]?.cus_phone,
    address: primarySaleOrderInvoiceData?.data[0]?.address,
    contact_name: primarySaleOrderInvoiceData?.data[0]?.contact_name,
    customer_address: primarySaleOrderInvoiceData?.data[0]?.customer_address,
    sub_total: primarySaleOrderInvoiceData?.data[0]?.sub_total,
    discount_amount: primarySaleOrderInvoiceData?.data[0]?.discount_amount,
    tax_amount: primarySaleOrderInvoiceData?.data[0]?.tax_amount,
    other_charges: primarySaleOrderInvoiceData?.data[0]?.other_charges,
    grand_total_amount:
      primarySaleOrderInvoiceData?.data[0]?.grand_total_amount,
    payment_type_name: primarySaleOrderInvoiceData?.data[0]?.payment_type_name,
    item_detail: primarySaleOrderInvoiceData?.data[0]?.item_detail,
    remark: primarySaleOrderInvoiceData?.data[0]?.remark,
  };
  // Helper function to chunk items into groups of 3
  const chunkArray = (arr: any[], size: number) => {
    const chunks = [];
    for (let i = 0; i < arr.length; i += size) {
      chunks.push(arr.slice(i, i + size));
    }
    return chunks;
  };

  const chunkedItems = chunkArray(retrieveValues?.item_detail || [], 3);
  useEffect(() => {
    const handleAfterPrint = () => navigate(-1);
    window.addEventListener("afterprint", handleAfterPrint);
    return () => window.removeEventListener("afterprint", handleAfterPrint);
  }, [navigate]);

  useEffect(() => {
    if (!isFetching && primarySaleOrderInvoiceData?.data?.length > 0) {
      // Wait a tiny bit to ensure DOM updates fully before printing
      const printTimer = setTimeout(() => {
        window.print();
      }, 300);

      return () => clearTimeout(printTimer);
    }
  }, [isFetching, primarySaleOrderInvoiceData]);
  return (
    <div className="print-page">
      <div className="invoice-container relative min-h-[calc(100vh-2rem)] pb-20">
        {/* Header Section */}
        <div className="header mb-4 text-left">
          {/* <h1 className="text-xl font-bold mb-2 text-[18px]">Mg Kaung</h1> */}
          <img
            width={60}
            height={50}
            className="mb-3"
            alt="Logo"
            src={logoWithText}
          />

          <p className="text-sm mb-2">{PRINT_TEXT.invoice_voucher_text_one}</p>
        </div>

        <div className="border-b border-black mb-2 w-full"></div>

        {/* Invoice Details */}
        <div className="invoice-details">
          <table className="details-table">
            <tbody>
              <tr>
                <td className="bill-to text-nowrap">
                  <p>
                    <strong>Shop Name:</strong> {retrieveValues?.customer_name}
                  </p>
                  <p>
                    <strong>Address:</strong> {retrieveValues?.address ?? ""}{" "}
                    {retrieveValues?.customer_phone ?? ""}
                  </p>
                  <p>
                    <strong>Payment Type: </strong>
                    {retrieveValues?.payment_type_name}
                  </p>
                </td>
                <td className="bill-to text-nowrap">
                  <p>
                    <strong>Invoice No: </strong> {retrieveValues?.SSIC}
                  </p>
                  <p>
                    <strong>Invoice Date: </strong>
                    {retrieveValues.invoice_date
                      ? formatDBDate(retrieveValues.invoice_date)
                      : "-"}
                  </p>
                  <p>
                    <strong>Due Date: </strong>
                    {retrieveValues.due_date
                      ? formatDBDate(retrieveValues.due_date)
                      : "-"}
                  </p>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Wrapper to prevent page break */}
        <div className="invoice-body mt-5">
          {/* Items Table */}
          <table className="items-table">
            <thead>
              <tr className="table-header text-center">
                <th className="text-center item-table-head"> NO</th>
                <th className="text-center item-table-description">
                  DESCRIPTION
                </th>
                <th className="text-center item-table-unit">UNIT</th>
                <th className="text-center item-table-qty">QTY</th>
                <th className="text-center item-table-price">PRICE</th>
                <th className="text-center item-table-amount">AMOUNT</th>
              </tr>
            </thead>
            <tbody className="text-center">
              {chunkedItems.map((group, groupIndex) => (
                <React.Fragment key={groupIndex}>
                  {group.map((item: any, index: number) => (
                    <tr key={index}>
                      <td className="item-table-head">{groupIndex + 1}</td>
                      <td>{item?.item_description}</td>
                      <td className="text-center">{item?.unit_name}</td>
                      <td className="text-center">{item?.qty}</td>
                      <td className="text-right">
                        {thousandSeperator(item?.price - item?.discount_amount)}
                      </td>
                      <td className="text-right">
                        {thousandSeperator(item?.amount)}
                      </td>
                    </tr>
                  ))}
                  {groupIndex < chunkedItems.length - 1 && (
                    <tr className="page-break-row">
                      <td colSpan={6} className="page-break"></td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>

          {/* Footer Section */}
          <div className="footer mt-6 flex justify-between items-start border-t border-black pt-2">
            {/* Left side - Remark */}
            <div className="w-1/2">
              <p className="whitespace-pre-line break-words text-left">
                <strong>Remark:</strong> {retrieveValues?.remark ?? "None"}
              </p>
            </div>

            {/* Right side - Totals */}
            <table className="w-auto">
              <tbody>
                <tr>
                  <td className="text-right pr-4 font-semibold">Sub Total:</td>
                  <td className="text-right">
                    {Number(retrieveValues?.sub_total || 0).toLocaleString()}
                  </td>
                </tr>
                <tr>
                  <td className="text-right pr-4 font-semibold">Discount:</td>
                  <td className="text-right">
                    {Number(
                      retrieveValues?.discount_amount || 0
                    ).toLocaleString()}
                  </td>
                </tr>
                <tr>
                  <td className="text-right pr-4 font-semibold">Tax:</td>
                  <td className="text-right">
                    {Number(retrieveValues?.tax_amount || 0).toLocaleString()}
                  </td>
                </tr>
                <tr>
                  <td className="text-right pr-4 font-semibold">
                    Other Charges:
                  </td>
                  <td className="text-right">
                    {Number(
                      retrieveValues?.other_charges || 0
                    ).toLocaleString()}
                  </td>
                </tr>
                <tr className="border-t border-black">
                  <td className="text-right pr-4 font-bold text-lg">
                    Grand Total:
                  </td>
                  <td className="text-right font-bold text-lg">
                    {Number(
                      retrieveValues?.grand_total_amount || 0
                    ).toLocaleString()}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className=" grid grid-cols-2 signature border-t border-black pt-2">
            <div className="signature-col text-center">
              <p className="font-semibold ">Sale Person</p>
              <p>{retrieveValues?.sales_person_name ?? "sdfdf"}</p>
              <div className="border-b border-black h-7 mb-1"></div>
            </div>
            <div className="signature-col text-center">
              <p className="font-semibold ">Check By</p>
              <p>{retrieveValues?.approved_person_name ?? ""}</p>
              <div className="border-b border-black h-7 mb-1"></div>
            </div>
          </div>
        </div>
        <div className="clear-both"></div>
        <div className="text-center mt-10 font-semibold thankyou">
          THANK YOU FOR YOUR COOPERATION WITH US!
        </div>
      </div>
    </div>
  );
};

export default DirectSaleOrderInvoicePrintPage;
